import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ExternalLink, TriangleAlert } from 'lucide-react';
import type { TrustDetailsView, TrustPointView } from './discoveryModel';

/**
 * Trust score chip for supplier rows/cards — mono per the data-type rule.
 * Renders ONLY a backend-provided score; when none exists the honest
 * absent-state says so instead of inventing a number.
 *
 * When research sent the reasons behind the score, the chip is a button that
 * opens them: what supports the score and what holds it back, each with its
 * source. The method (weights, formula) is never shown — only the evidence.
 */
export interface TrustChipProps {
  score?: number;
  trust?: TrustDetailsView;
  className?: string;
}

// Same thresholds as the score bands: Platinum 85+, Gold 70+.
const toneFor = (score: number): string => {
  if (score >= 85) return 'border-action-border bg-action-bg text-action-deep';
  if (score >= 70) return 'border-neutral-border bg-neutral-bg text-neutral-ink';
  return 'border-warn-border bg-warn-bg text-warn-ink';
};

const CONFIDENCE_LABEL: Record<string, string> = {
  low: 'Limited evidence',
  medium: 'Moderate evidence',
  high: 'Well evidenced',
};

const GAP = 6;
const EDGE = 8;

const Point: React.FC<{ point: TrustPointView; kind: 'support' | 'concern' }> = ({ point, kind }) => (
  <li className="flex items-start gap-s2">
    {kind === 'support' ? (
      <Check className="mt-[2px] h-3 w-3 shrink-0 text-success-ink" aria-hidden />
    ) : (
      <TriangleAlert className="mt-[2px] h-3 w-3 shrink-0 text-warn-ink" aria-hidden />
    )}
    <span className="text-ink-body">
      {point.text}
      {point.sourceUrl && (
        <a
          href={point.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-s1 inline-flex items-center text-ink-muted hover:text-ink"
          aria-label="Source"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-[10px] w-[10px]" />
        </a>
      )}
    </span>
  </li>
);

const TrustDetails: React.FC<{ score: number | undefined; trust: TrustDetailsView }> = ({ score, trust }) => (
  <div className="flex flex-col gap-s3">
    <div className="flex flex-wrap items-baseline gap-s2">
      <span className="font-mono text-[15px] font-bold text-ink">{score ?? '—'}</span>
      {trust.band && <span className="text-[12px] font-semibold text-ink">{trust.band}</span>}
      {trust.confidence && (
        <span className="rounded-pill border border-neutral-border bg-neutral-bg px-s2 py-[1px] text-meta text-ink-muted">
          {CONFIDENCE_LABEL[trust.confidence]}
        </span>
      )}
    </div>
    {trust.summary && <p className="text-[12px] leading-[1.45] text-ink-secondary">{trust.summary}</p>}
    {trust.supports.length > 0 && (
      <div>
        <div className="mb-s1 text-meta font-semibold uppercase tracking-[0.04em] text-ink-muted">What supports it</div>
        <ul className="flex flex-col gap-s1 text-[12px] leading-[1.45]">
          {trust.supports.map((p) => <Point key={p.text} point={p} kind="support" />)}
        </ul>
      </div>
    )}
    {trust.concerns.length > 0 && (
      <div>
        <div className="mb-s1 text-meta font-semibold uppercase tracking-[0.04em] text-ink-muted">What holds it back</div>
        <ul className="flex flex-col gap-s1 text-[12px] leading-[1.45]">
          {trust.concerns.map((p) => <Point key={p.text} point={p} kind="concern" />)}
        </ul>
      </div>
    )}
  </div>
);

export const TrustChip: React.FC<TrustChipProps> = ({ score, trust, className = '' }) => {
  const id = useId();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const b = panel.getBoundingClientRect();
    const below = a.bottom + b.height + GAP <= window.innerHeight - EDGE || a.top - b.height - GAP < EDGE;
    const top = below ? a.bottom + GAP : a.top - b.height - GAP;
    const maxLeft = Math.max(EDGE, window.innerWidth - b.width - EDGE);
    const left = Math.min(Math.max(a.left, EDGE), maxLeft);
    // The app renders under a CSS `zoom` (see ui/Tooltip): convert visual pixels to the panel's own.
    const scale = panel.offsetWidth > 0 ? b.width / panel.offsetWidth : 1;
    setPos({ top: top / scale, left: left / scale });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!panelRef.current?.contains(target) && !anchorRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        anchorRef.current?.focus();
      }
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  const blocked = trust?.status === 'blocked';
  const review = trust?.status === 'review';

  if (score == null && !blocked) {
    return (
      <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-pill border border-neutral-border bg-neutral-bg px-s2 py-[1px] text-meta text-ink-muted ${className}`}>
        No score yet
      </span>
    );
  }

  const label = blocked ? 'Not eligible' : `Trust ${score}`;
  const tone = blocked ? 'border-danger-border bg-danger-bg text-danger-ink' : toneFor(score as number);
  const chipClass = `inline-flex shrink-0 items-center gap-[3px] whitespace-nowrap rounded-pill border px-s2 py-[1px] font-mono text-meta font-semibold ${tone} ${className}`;

  if (!trust) return <span className={chipClass}>{label}</span>;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`${chipClass} cursor-pointer underline decoration-dotted underline-offset-2 hover:brightness-95`}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        title="Why this score?"
        onClick={(e) => {
          // Chips sit inside clickable rows and cards.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {label}
        {review && <TriangleAlert className="h-[10px] w-[10px]" aria-label="Flagged for review" />}
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={id}
            role="dialog"
            aria-label="Why this trust score"
            className="fixed left-0 top-0 z-50 w-[min(340px,calc(100vw-16px))] rounded-card border border-line bg-surface p-s4 text-left shadow-floating"
            style={{ transform: pos ? `translate(${pos.left}px, ${pos.top}px)` : undefined, visibility: pos ? 'visible' : 'hidden' }}
            onClick={(e) => e.stopPropagation()}
          >
            <TrustDetails score={score} trust={trust} />
          </div>,
          document.body
        )}
    </>
  );
};

export default TrustChip;
